import { clear, createStore, del, get, set } from 'idb-keyval';
import { apiClient } from '@/api/client';

const newsGalleryImageStore = createStore(
  'eklase-news-gallery-images',
  'blobs',
);

const NEWS_GALLERY_IMAGE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CachedNewsGalleryImage {
  blob: Blob;
  cachedAt: number;
}

const inFlightRequests = new Map<string, Promise<Blob | undefined>>();

const buildCacheKey = (imageId: string, size: number | null): string =>
  size === null ? imageId : `${imageId}:${size}`;

const readCachedBlob = async (
  imageId: string,
  size: number | null,
): Promise<Blob | undefined> => {
  const key = buildCacheKey(imageId, size);
  const cached = await get<CachedNewsGalleryImage>(
    key,
    newsGalleryImageStore,
  ).catch(() => undefined);

  if (!cached) {
    return undefined;
  }

  const isExpired =
    Date.now() - cached.cachedAt > NEWS_GALLERY_IMAGE_CACHE_TTL_MS;

  if (isExpired) {
    del(key, newsGalleryImageStore).catch(() => undefined);
    return undefined;
  }

  return cached.blob;
};

const writeCachedBlob = (
  imageId: string,
  size: number | null,
  blob: Blob,
): Promise<void> =>
  set(
    buildCacheKey(imageId, size),
    { blob, cachedAt: Date.now() },
    newsGalleryImageStore,
  ).catch(() => undefined);

const fetchImageBlob = async (
  imageId: string,
  size: number | null,
): Promise<Blob> => {
  const path =
    size === null
      ? `/gallery-images/${imageId}`
      : `/gallery-images/${imageId}/thumbnail`;
  const params = size === null ? undefined : { size };
  const response = await apiClient.get<Blob>(path, {
    params,
    responseType: 'blob',
  });

  return response.data;
};

export const loadNewsGalleryImage = (
  imageId: string,
  size: number | null,
): Promise<Blob | undefined> => {
  const key = buildCacheKey(imageId, size);
  const pending = inFlightRequests.get(key);

  if (pending) {
    return pending;
  }

  const request = (async () => {
    const cached = await readCachedBlob(imageId, size);

    if (cached) {
      return cached;
    }

    const blob = await fetchImageBlob(imageId, size);
    writeCachedBlob(imageId, size, blob);

    return blob;
  })().catch(() => undefined);

  inFlightRequests.set(key, request);
  request.finally(() => inFlightRequests.delete(key));

  return request;
};

export const clearNewsGalleryImageCache = (): Promise<void> =>
  clear(newsGalleryImageStore).catch(() => undefined);
